import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './api';

// Stale-data warning.
//
// Polls /api/data-health (backend/dataHealth.js) and shows a red bar across
// the top when the screener.in fundamentals export has stopped arriving, so a
// broken scraper is noticed the same day instead of after a week of analysis
// on old numbers. Dismissing hides it only until the feed's state changes
// (a newer failure or a newer file), so it can't be silenced for good.
const POLL_MS = 5 * 60 * 1000;
const DISMISS_KEY = 'bse5-data-health-dismissed';

const stateId = (feed) => `${feed.key}|${feed.updatedAt}|${feed.lastRunError || ''}`;

const formatAge = (minutes) => {
  if (minutes === null || minutes === undefined) return 'never';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
};

const barStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, zIndex: 300,
  display: 'flex', alignItems: 'center', gap: 12, padding: '6px 12px',
  background: '#fef3f2', borderBottom: '1px solid #fda29b', color: '#b42318',
  fontSize: 12, boxShadow: '0 2px 8px rgba(16,24,40,.12)',
};
const closeStyle = {
  marginLeft: 'auto', border: 'none', background: 'none', color: '#b42318',
  cursor: 'pointer', fontSize: 14, padding: '0 4px',
};

const DataHealthBanner = () => {
  const [staleFeeds, setStaleFeeds] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) || ''; } catch { return ''; }
  });

  const load = useCallback(async () => {
    try {
      const response = await apiFetch('/api/data-health');
      if (!response.ok) return;
      const body = await response.json();
      setStaleFeeds((body.feeds || []).filter((feed) => feed.stale));
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const interval = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(interval);
  }, [load]);

  const currentId = staleFeeds.map(stateId).join(',');
  if (!staleFeeds.length || dismissed === currentId) return null;

  const dismiss = () => {
    setDismissed(currentId);
    try { sessionStorage.setItem(DISMISS_KEY, currentId); } catch {}
  };

  return (
    <div style={barStyle} role="alert">
      <div>
        {staleFeeds.map((feed) => (
          <div key={feed.key}>
            <strong>⚠ Stale data:</strong> {feed.label} last updated {formatAge(feed.ageMinutes)}
            {feed.updatedAt && ` (${new Date(feed.updatedAt).toLocaleString()})`}.
            {feed.lastRunError && <> Last run failed: {feed.lastRunError.replace(/\.?$/, '.')}</>}
            {' '}Fundamentals, market cap and quarterly profit figures may be out of date.
          </div>
        ))}
      </div>
      <button type="button" style={closeStyle} onClick={dismiss} aria-label="Dismiss">✕</button>
    </div>
  );
};

export default DataHealthBanner;
